import React from 'react';
import { Calendar, ArrowRight, Clock, Users, CheckCircle } from 'lucide-react';

/**
 * OnboardingScheduler Component
 * 
 * Displays after White Label order bump to schedule onboarding call.
 * Matches the dark gradient style of the signup flow.
 * 
 * Props:
 * - onScheduled: () => void - called when user schedules or skips
 * - userEmail: string - user's email for calendar embed
 * - userName: string - user's name for personalization
 */
const OnboardingScheduler = ({ onScheduled, userEmail = '', userName = '' }) => {
  
  const handleSkip = () => {
    onScheduled?.();
  };

  const benefits = [
    {
      icon: <Users className="w-5 h-5" />,
      text: "Meet your dedicated success manager"
    },
    {
      icon: <CheckCircle className="w-5 h-5" />,
      text: "Get your account configured for your business"
    },
    {
      icon: <Clock className="w-5 h-5" />,
      text: "Be live and delivering within days"
    }
  ];

  return (
    <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-purple-500/30">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <Calendar className="w-8 h-8 text-purple-400" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">
          Schedule Your Onboarding
        </h2>
        <p className="text-purple-200">
          {userName ? `${userName}, let's` : "Let's"} get you set up for success
        </p>
      </div>

      {/* Benefits */}
      <div className="grid gap-3 mb-8">
        {benefits.map((benefit, index) => (
          <div key={index} className="flex items-center gap-3 text-purple-200">
            <div className="flex-shrink-0 w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center text-purple-400">
              {benefit.icon}
            </div>
            <span>{benefit.text}</span>
          </div>
        ))}
      </div>

      {/* Calendar Embed Placeholder */}
      <div className="bg-white/5 rounded-xl p-8 mb-6 border border-purple-500/20">
        <div className="text-center">
          <Calendar className="w-12 h-12 text-purple-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">
            Onboarding Calendar
          </h3>
          <p className="text-purple-300 text-sm mb-4">
            Select a time that works best for you
          </p>
          
          {/* Placeholder for calendar widget */}
          <div className="bg-purple-500/10 rounded-lg p-6 border border-dashed border-purple-500/30">
            <p className="text-purple-300 text-sm">
              📅 Calendar scheduling widget
            </p>
            <p className="text-purple-400/60 text-xs mt-2">
              HighLevel calendar integration
            </p>
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 mb-6">
        <p className="text-blue-200 text-sm text-center">
          <strong>What to expect:</strong> A 30-45 minute call where we configure Athena 
          for your specific business needs and show you how to hit the ground running.
        </p>
      </div>

      {/* Actions */}
      <div className="space-y-4">
        <button
          onClick={onScheduled}
          className="w-full py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-xl hover:opacity-90 transition-all flex items-center justify-center gap-2"
        >
          <span>Continue to Dashboard</span>
          <ArrowRight className="w-5 h-5" />
        </button>
        
        <p className="text-center text-purple-400/60 text-xs">
          Can&apos;t find a time? No worries - we&apos;ll reach out to schedule.
        </p>
      </div>
    </div>
  );
};

export default OnboardingScheduler;
